import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  Download,
  Calendar,
  Briefcase,
  CheckCircle2,
  Clock,
  IndianRupee,
} from 'lucide-react';
import { formatPaise } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@shared/api/client';
import { downloadAnalyticsReport } from '@shared/api/associates';
import { useAssociateAnalytics, useAssociateAnalyticsSeries } from '@shared/hooks/use-associate';
import type { AnalyticsMonth } from '@shared/types/api';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function AnalyticsPage() {
  const now = new Date();
  const thisYear = now.getUTCFullYear();
  const [year, setYear] = useState(thisYear);
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));

  const { data, isLoading } = useAssociateAnalytics();
  const { data: series, isLoading: seriesLoading } = useAssociateAnalyticsSeries(year);

  const download = useMutation({
    mutationFn: async () => {
      const blob = await downloadAnalyticsReport(from, to);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${from}-${to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      {/* Header with CSV Report Export Range */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-600">Overview of client transactions, service volume, and earnings.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5 shadow-2xs">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 px-1">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span>Report Range:</span>
            </div>
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <span className="text-gray-400">From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded border border-gray-300 bg-gray-50 px-2 py-1 text-xs text-gray-800 focus:bg-white focus:outline-hidden focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <span className="text-gray-400">To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded border border-gray-300 bg-gray-50 px-2 py-1 text-xs text-gray-800 focus:bg-white focus:outline-hidden focus:ring-1 focus:ring-brand-500"
              />
            </label>
            <Button
              onClick={() => download.mutate()}
              disabled={download.isPending}
              className="h-8 gap-1.5 px-3 py-1 text-xs bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-xs"
            >
              <Download className="h-3.5 w-3.5" />
              {download.isPending ? 'Preparing…' : 'Download Report'}
            </Button>
          </div>
        </div>
      </div>

      {download.isError ? (
        <p className="mt-2 text-sm text-red-600">{apiErrorMessage(download.error)}</p>
      ) : null}

      {/* Primary KPI Cards */}
      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Total Services Assigned"
          value={data?.assignedAllTime ?? 0}
          delta={data?.momDelta.assigned}
          loading={isLoading}
          icon={Briefcase}
        />
        <Kpi
          label="Total Services Completed"
          value={data?.completedAllTime ?? 0}
          delta={data?.momDelta.completed}
          loading={isLoading}
          icon={CheckCircle2}
        />
        <Kpi
          label="Total Services Pending"
          value={data?.pendingAllTime ?? 0}
          delta={data?.momDelta.pending}
          loading={isLoading}
          icon={Clock}
        />
        <Kpi
          label="Total Earnings"
          value={data ? formatPaise(data.lifetimePayoutPaise) : '—'}
          delta={data?.momDelta.earningsPaise}
          loading={isLoading}
          icon={IndianRupee}
        />
      </section>

      {/* Performance Overview (Earnings Line Chart) */}
      <section className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-2xs">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Performance Overview</h2>
            <p className="text-xs text-gray-500">Total earnings breakdown by month</p>
          </div>
          <YearSelect year={year} onChange={setYear} />
        </div>
        <EarningsLineChart year={year} months={series?.months} loading={seriesLoading} />
      </section>

      {/* Services Overview (Completed vs Pending Bar Chart) */}
      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-2xs">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Services Overview</h2>
            <p className="text-xs text-gray-500">Monthly breakdown of completed and pending services</p>
          </div>
          <YearSelect year={year} onChange={setYear} />
        </div>
        <ServicesBarChart months={series?.months} loading={seriesLoading} />
      </section>

      {/* Secondary Current-Month Snapshot & Settlement Info */}
      <section className="mt-8">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Monthly Snapshot & Payouts</h2>
          <p className="text-xs text-gray-500">Current month activity and unsettled earnings balance</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Kpi
            label="Completed This Month"
            value={data?.completedThisMonth ?? 0}
            loading={isLoading}
            icon={CheckCircle2}
          />
          <Kpi
            label="Pending Payout (Unsettled)"
            value={data ? formatPaise(data.pendingPayoutPaise) : '—'}
            loading={isLoading}
            icon={Clock}
          />
        </div>
      </section>
    </div>
  );
}

function YearSelect({ year, onChange }: { year: number; onChange: (y: number) => void }) {
  const thisYear = new Date().getUTCFullYear();
  const options = [thisYear, thisYear - 1, thisYear - 2];
  return (
    <select
      value={year}
      onChange={(e) => onChange(Number(e.target.value))}
      className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 shadow-2xs focus:border-brand-500 focus:outline-hidden focus:ring-1 focus:ring-brand-500"
    >
      {options.map((y) => (
        <option key={y} value={y}>
          {y}
        </option>
      ))}
    </select>
  );
}

function Kpi({
  label,
  value,
  delta,
  loading,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  delta?: number;
  loading?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  const hasDelta = typeof delta === 'number';
  const isZero = delta === 0;
  const isPositive = (delta ?? 0) > 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-2xs">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        {Icon ? (
          <div className="rounded-lg bg-gray-50 p-1.5 text-gray-400">
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">
        {loading ? <span className="animate-pulse text-gray-300">…</span> : value}
      </div>
      {hasDelta ? (
        <div
          className={`mt-1.5 flex items-center gap-1 text-xs font-medium ${
            isZero
              ? 'text-gray-500'
              : isPositive
              ? 'text-emerald-700'
              : 'text-rose-600'
          }`}
        >
          {isZero ? (
            <span>0% from last month</span>
          ) : (
            <>
              {isPositive ? (
                <TrendingUp className="h-3.5 w-3.5" />
              ) : (
                <TrendingDown className="h-3.5 w-3.5" />
              )}
              <span>{Math.abs(delta)}% from last month</span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function EarningsLineChart({
  year,
  months,
  loading,
}: {
  year: number;
  months?: AnalyticsMonth[];
  loading?: boolean;
}) {
  if (loading) return <Placeholder text="Loading earnings data…" />;

  const values = months ? months.map((m) => Number(m.earningsPaise)) : [];
  const hasEarnings = values.some((v) => v > 0);
  const max = Math.max(...values, 100000); // at least ₹1,000 baseline
  const width = 720;
  const height = 200;
  const padding = { top: 15, right: 15, bottom: 25, left: 55 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const stepX = chartW / 11;

  if (!months || !hasEarnings) {
    return (
      <div className="mt-4 relative overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-4xl" preserveAspectRatio="xMidYMid meet">
          {/* Horizontal grid lines */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left + chartW}
            y2={padding.top}
            stroke="#f3f4f6"
            strokeDasharray="4 4"
          />
          <line
            x1={padding.left}
            y1={padding.top + chartH / 2}
            x2={padding.left + chartW}
            y2={padding.top + chartH / 2}
            stroke="#f3f4f6"
            strokeDasharray="4 4"
          />
          <line
            x1={padding.left}
            y1={padding.top + chartH}
            x2={padding.left + chartW}
            y2={padding.top + chartH}
            stroke="#e5e7eb"
          />

          {/* Y-axis zero */}
          <text
            x={padding.left - 8}
            y={padding.top + chartH + 3}
            fontSize="10"
            textAnchor="end"
            fill="#9ca3af"
          >
            ₹0
          </text>

          {/* Month labels */}
          {MONTH_LABELS.map((label, i) => (
            <text
              key={label}
              x={padding.left + i * stepX}
              y={height - 6}
              fontSize="10"
              textAnchor="middle"
              fill="#9ca3af"
            >
              {label}
            </text>
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-6">
          <p className="text-xs font-medium text-gray-500">
            No paid earnings recorded for {year} yet.
          </p>
          <p className="text-2xs text-gray-400 mt-0.5">
            Earnings reflect here once admin clears payouts for completed services.
          </p>
        </div>
      </div>
    );
  }

  const points = values.map((v, i) => {
    const x = padding.left + i * stepX;
    const y = padding.top + chartH - (v / max) * chartH;
    return { x, y, v };
  });

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${
    padding.top + chartH
  } Z`;

  return (
    <div className="mt-4 overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-4xl" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="earningsArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#15803d" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#15803d" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Horizontal gridlines */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left + chartW}
          y2={padding.top}
          stroke="#f3f4f6"
          strokeDasharray="4 4"
        />
        <text
          x={padding.left - 8}
          y={padding.top + 4}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          ₹{Math.round(max / 100).toLocaleString('en-IN')}
        </text>

        <line
          x1={padding.left}
          y1={padding.top + chartH / 2}
          x2={padding.left + chartW}
          y2={padding.top + chartH / 2}
          stroke="#f3f4f6"
          strokeDasharray="4 4"
        />
        <text
          x={padding.left - 8}
          y={padding.top + chartH / 2 + 4}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          ₹{Math.round(max / 200).toLocaleString('en-IN')}
        </text>

        <line
          x1={padding.left}
          y1={padding.top + chartH}
          x2={padding.left + chartW}
          y2={padding.top + chartH}
          stroke="#e5e7eb"
        />
        <text
          x={padding.left - 8}
          y={padding.top + chartH + 3}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          ₹0
        </text>

        {/* Filled Area */}
        <path d={areaD} fill="url(#earningsArea)" />

        {/* Line */}
        <path d={pathD} fill="none" stroke="#15803d" strokeWidth={2.5} />

        {/* Points with tooltip title */}
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={3.5}
            fill="#ffffff"
            stroke="#15803d"
            strokeWidth={2}
            className="cursor-pointer transition-transform hover:scale-125"
          >
            <title>
              {MONTH_LABELS[i]}: ₹{(p.v / 100).toLocaleString('en-IN')}
            </title>
          </circle>
        ))}

        {/* Month labels */}
        {MONTH_LABELS.map((label, i) => (
          <text
            key={label}
            x={padding.left + i * stepX}
            y={height - 6}
            fontSize="10"
            textAnchor="middle"
            fill="#6b7280"
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function ServicesBarChart({ months, loading }: { months?: AnalyticsMonth[]; loading?: boolean }) {
  if (loading) return <Placeholder text="Loading services data…" />;
  if (!months || months.every((m) => m.completed === 0 && m.pending === 0)) {
    return <Placeholder text="No service activity recorded for this year yet." />;
  }

  const maxVal = Math.max(...months.map((m) => Math.max(m.completed, m.pending)));
  // Round up to clean ceiling (e.g. 5, 10, 20, 50)
  const max = maxVal <= 5 ? 5 : maxVal <= 10 ? 10 : Math.ceil(maxVal / 10) * 10;
  const width = 720;
  const height = 220;
  const padding = { top: 15, right: 15, bottom: 28, left: 35 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const group = chartW / 12;
  const barW = Math.max(5, Math.min(16, group / 3.2));

  return (
    <div className="mt-4 overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-4xl" preserveAspectRatio="xMidYMid meet">
        {/* Horizontal gridlines and Y-axis scale */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left + chartW}
          y2={padding.top}
          stroke="#f3f4f6"
          strokeDasharray="4 4"
        />
        <text
          x={padding.left - 6}
          y={padding.top + 4}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          {max}
        </text>

        <line
          x1={padding.left}
          y1={padding.top + chartH / 2}
          x2={padding.left + chartW}
          y2={padding.top + chartH / 2}
          stroke="#f3f4f6"
          strokeDasharray="4 4"
        />
        <text
          x={padding.left - 6}
          y={padding.top + chartH / 2 + 4}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          {Math.round(max / 2)}
        </text>

        <line
          x1={padding.left}
          y1={padding.top + chartH}
          x2={padding.left + chartW}
          y2={padding.top + chartH}
          stroke="#e5e7eb"
        />
        <text
          x={padding.left - 6}
          y={padding.top + chartH + 3}
          fontSize="10"
          textAnchor="end"
          fill="#9ca3af"
        >
          0
        </text>

        {/* Grouped Bars */}
        {months.map((m, i) => {
          const cx = padding.left + i * group + group / 2;
          const cH = (m.completed / max) * chartH;
          const pH = (m.pending / max) * chartH;
          return (
            <g key={m.month}>
              {/* Completed bar (Green) */}
              {m.completed > 0 ? (
                <rect
                  x={cx - barW - 1.5}
                  y={padding.top + chartH - cH}
                  width={barW}
                  height={cH}
                  rx={2}
                  fill="#15803d"
                  className="transition-opacity hover:opacity-85"
                >
                  <title>{MONTH_LABELS[i]} - Completed: {m.completed}</title>
                </rect>
              ) : null}

              {/* Pending bar (Yellow/Amber) */}
              {m.pending > 0 ? (
                <rect
                  x={cx + 1.5}
                  y={padding.top + chartH - pH}
                  width={barW}
                  height={pH}
                  rx={2}
                  fill="#eab308"
                  className="transition-opacity hover:opacity-85"
                >
                  <title>{MONTH_LABELS[i]} - Pending: {m.pending}</title>
                </rect>
              ) : null}

              {/* Month label */}
              <text x={cx} y={height - 10} fontSize="10" textAnchor="middle" fill="#6b7280">
                {MONTH_LABELS[i]}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="mt-3 flex items-center justify-end gap-5 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-xs bg-[#15803d]" />
          Completed Services
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-xs bg-[#eab308]" />
          Pending / In-Progress Services
        </span>
      </div>
    </div>
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div className="mt-4 flex h-44 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 text-sm text-gray-500">
      {text}
    </div>
  );
}

